import { useState } from "react";
import { useSearchParams } from "react-router";
import { NewSessionDialog } from "../components/Dialogs.tsx";
import { SessionsTable } from "../components/SessionsTable.tsx";
import { Empty, ErrorBox, Loading } from "../components/ui.tsx";
import { useApi } from "../lib/api.ts";
import { STATE_LABELS } from "../lib/format.ts";
import type { Project, Session, SessionState } from "../lib/types.ts";

/** Filter groups match the overview tiles. */
const GROUPS: Record<string, SessionState[]> = {
  waiting: ["waiting"],
  working: ["working", "rate_limited"],
  idle: ["idle", "done", "queued", "created"],
  errored: ["errored"],
  stopped: ["stopped"],
};

export function Sessions() {
  const sessions = useApi<Session[]>("/api/sessions");
  const projects = useApi<Project[]>("/api/projects");
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const state = params.get("state") ?? "";
  const project = params.get("project") ?? "";
  const q = params.get("q") ?? "";
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  if (!sessions.data) return sessions.error ? <ErrorBox error={sessions.error} /> : <Loading />;
  const filtered = sessions.data.filter(
    (s) =>
      (!state || (GROUPS[state] ?? [state]).includes(s.state)) &&
      (!project || s.projectId === project) &&
      (!q || s.name.includes(q.toLowerCase()) || s.branch.includes(q.toLowerCase())),
  );

  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Sessions</h1>
          <div className="sub">Every Claude Code session, newest first.</div>
        </div>
        <button className="btn primary" disabled={!projects.data?.length} onClick={() => setCreating(true)}>
          New session
        </button>
      </div>
      <div className="row">
        <select style={{ width: "auto" }} value={state} onChange={(e) => setParam("state", e.target.value)}>
          <option value="">All states</option>
          <option value="waiting">Waiting on you</option>
          <option value="working">Running</option>
          <option value="idle">Idle / done</option>
          <option value="errored">{STATE_LABELS.errored}</option>
          <option value="stopped">{STATE_LABELS.stopped}</option>
        </select>
        <select style={{ width: "auto" }} value={project} onChange={(e) => setParam("project", e.target.value)}>
          <option value="">All projects</option>
          {projects.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input style={{ width: 220 }} placeholder="Search name or branch" value={q} onChange={(e) => setParam("q", e.target.value)} />
        <span className="muted small">
          {filtered.length} of {sessions.data.length}
        </span>
      </div>
      <div className="card flush">
        {filtered.length ? (
          <SessionsTable sessions={filtered} projects={projects.data} />
        ) : (
          <Empty title={sessions.data.length ? "No sessions match" : "No sessions yet"} />
        )}
      </div>
      {creating && <NewSessionDialog onClose={() => setCreating(false)} />}
    </>
  );
}
